import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PublishedEvent, Scoreboard } from '../../@types';
import { QUERY_KEYS, USED_FOR } from '../../constants';
import { MessageBetType } from '../../helpers/betslipMessageParser';
import eventUpdater, { PublishedEventWithMarketsAndScores } from '../../helpers/eventUpdater';
import useWebsocket, { MessageType } from '../useWebsocket';
import { useGetEventScoreboard, useMarketGroups, usePublishedMarkets } from '../../queries';
import { useMarketGroupNavigation } from '../useMarketGroupNavigation';
import { getData } from '../../utils/api';

type ScoreboardStatus = 'loading' | 'ready' | 'unavailable';
type MarketsStatus = 'loading' | 'ready' | 'error';

const getScoreboardStatus = ({
  isEventLoading,
  hasEvent,
  isPending,
  isError,
  isSuccess,
  hasScoreboard,
}: {
  isEventLoading: boolean;
  hasEvent: boolean;
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
  hasScoreboard: boolean;
}): ScoreboardStatus => {
  if (isEventLoading || (hasEvent && isPending)) return 'loading';
  if (isError || (isSuccess && !hasScoreboard)) return 'unavailable';

  return 'ready';
};

const getMarketsStatus = ({
  isEventLoading,
  areMarketGroupsLoading,
  isSelectingInitialMarketGroup,
  isLoading,
  isSuccess,
  isError,
}: {
  isEventLoading: boolean;
  areMarketGroupsLoading: boolean;
  isSelectingInitialMarketGroup: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
}): MarketsStatus => {
  // Disabled and pending market queries both expose an empty array. Wait for an
  // explicit success before allowing the page to render the genuine empty state.
  if (
    isEventLoading ||
    areMarketGroupsLoading ||
    isSelectingInitialMarketGroup ||
    isLoading ||
    (!isSuccess && !isError)
  ) {
    return 'loading';
  }

  return isError ? 'error' : 'ready';
};

export const useInPlayEventPage = () => {
  // A WebSocket override belongs to one exact query-derived event snapshot.
  // When the route or market group changes, a new base object discards stale updates.
  const [liveEventState, setLiveEventState] = useState<{
    baseEvent: PublishedEventWithMarketsAndScores;
    event: PublishedEventWithMarketsAndScores;
  }>();
  const queryClient = useQueryClient();
  const scoreboardRef = useRef<Scoreboard | null>(null);
  const eventSnapshotRef = useRef<
    | {
        baseEvent: PublishedEventWithMarketsAndScores;
        event: PublishedEventWithMarketsAndScores;
      }
    | undefined
  >(undefined);

  const { eventId, marketGroupId } = useParams();
  const navigate = useNavigate();

  const {
    data: publishedEvent,
    isError: isEventError,
    isPending: isEventLoading,
  } = useQuery({
    queryKey: [QUERY_KEYS.publishedEvents, eventId],
    queryFn: (): Promise<{ item: PublishedEvent }> =>
      getData(`events/${eventId}/published`, {
        isLive: true,
      }),
    enabled: !!eventId,
    select: (data) => data.item,
    staleTime: Infinity,
  });

  const sportId = publishedEvent?.tournament?.sport?.id;

  const { data: marketGroups, isPending: areMarketGroupsLoading } = useMarketGroups(sportId, {
    usedFor: [USED_FOR.IN_PLAY],
  });

  useMarketGroupNavigation({ marketGroupId, marketGroups });

  const {
    data: scoreboard,
    refetch: refetchScoreboard,
    isPending: isScoreboardPending,
    isError: isScoreboardError,
    isSuccess: isScoreboardSuccess,
  } = useGetEventScoreboard(publishedEvent?.id ?? '');

  const {
    markets,
    isFetchingNextPage,
    lastElementRef,
    hasNextPage,
    isLoading,
    isInitialLoading,
    isFetching,
    isError: isMarketsError,
    isSuccess: isMarketsSuccess,
  } = usePublishedMarkets({
    eventId,
    marketGroupId,
    isLive: true,
  });

  const baseEvent = useMemo<PublishedEventWithMarketsAndScores | undefined>(
    () => (publishedEvent ? { ...publishedEvent, markets } : undefined),
    [markets, publishedEvent]
  );
  const event = liveEventState && liveEventState.baseEvent === baseEvent ? liveEventState.event : baseEvent;

  useEffect(() => {
    scoreboardRef.current = scoreboard ?? null;
  }, [scoreboard]);

  useEffect(() => {
    eventSnapshotRef.current = baseEvent && event ? { baseEvent, event } : undefined;
  }, [baseEvent, event]);

  const updaterCallback = useCallback(
    (data: Record<string, MessageType<MessageBetType>>[]) => {
      const snapshot = eventSnapshotRef.current;

      if (!snapshot) return;

      let needsRefetch = false;
      let newScoreboardState: Scoreboard | null = null;
      let updatedEvent = { ...snapshot.event };

      data.forEach((record) => {
        const eventData = record[snapshot.event.id];

        if (!eventData) return;

        updatedEvent = eventUpdater(
          {
            ...updatedEvent,
            eventPartScores: scoreboardRef.current?.eventPartScores,
            mainEventPart: scoreboardRef.current?.mainEventPart,
          },
          eventData,
          () => {
            needsRefetch = true;
          }
        );

        const shouldUpdateScoreboard =
          (eventData.event === 'EVENT_PART_SCORE' && updatedEvent.eventPartScores) ||
          (eventData.event === 'EVENT' && 'statistics' in eventData.payload);

        if (shouldUpdateScoreboard && scoreboardRef.current) {
          const baseScoreboard = newScoreboardState ?? scoreboardRef.current;
          newScoreboardState = {
            ...baseScoreboard,
            mainEventPart: updatedEvent.mainEventPart ?? baseScoreboard.mainEventPart,
            eventPartScores: updatedEvent.eventPartScores ?? baseScoreboard.eventPartScores,
            statistics: eventData.payload.statistics ?? baseScoreboard.statistics,
          };
        }
      });

      if (newScoreboardState) {
        scoreboardRef.current = newScoreboardState;
        // The scoreboard renders query data, so update that source directly instead
        // of mirroring it through local state and making it lag by another render.
        queryClient.setQueryData([QUERY_KEYS.eventPartScores, snapshot.event.id], newScoreboardState);
      }

      if (needsRefetch) {
        refetchScoreboard();
      }

      const nextLiveEventState = {
        baseEvent: snapshot.baseEvent,
        event: updatedEvent,
      };

      eventSnapshotRef.current = nextLiveEventState;
      setLiveEventState(nextLiveEventState);
    },
    [queryClient, refetchScoreboard]
  );

  const { joinRoom } = useWebsocket({
    callback: updaterCallback,
  });

  useEffect(() => {
    // Live scores and event status do not depend on markets. Joining here removes
    // the former market-loading waterfall and avoids missing early room messages.
    if (publishedEvent?.id) {
      joinRoom(publishedEvent.id);
    }
  }, [joinRoom, publishedEvent?.id]);

  const handleActiveGroupChange = useCallback(
    (groupId: string | undefined) => {
      navigate(`/in-play/match-view/${eventId}/${groupId}`, { replace: true });
    },
    [navigate, eventId]
  );

  const isSelectingInitialMarketGroup = marketGroups !== undefined && !marketGroupId;
  const scoreboardStatus = getScoreboardStatus({
    isEventLoading,
    hasEvent: Boolean(publishedEvent?.id),
    isPending: isScoreboardPending,
    isError: isScoreboardError,
    isSuccess: isScoreboardSuccess,
    hasScoreboard: Boolean(scoreboard),
  });
  const marketsStatus = getMarketsStatus({
    isEventLoading,
    areMarketGroupsLoading: Boolean(sportId) && areMarketGroupsLoading,
    isSelectingInitialMarketGroup,
    isLoading: isInitialLoading || isLoading,
    isSuccess: isMarketsSuccess,
    isError: isMarketsError,
  });
  const isMarketsRefreshing = marketsStatus === 'ready' && isFetching;

  return {
    event,
    scoreboardEvent: event ?? publishedEvent,
    marketGroupId,
    scoreboardState: scoreboard ?? null,
    scoreboardStatus,
    marketsStatus,
    isMarketsRefreshing,
    isLoadingScoreboard: scoreboardStatus === 'loading',
    marketGroups,
    handleActiveGroupChange,
    markets,
    isFetchingNextPage,
    lastElementRef,
    hasNextPage,
    isLoading,
    isInitialLoading,
    areMarketGroupsLoading,
    isEventError,
    isFetching,
  };
};
